import React, { useEffect, useState, useRef } from "react";
import Dropdown from "../../components/Form/Dropdown";
import { cities, categories } from "../../constants/commonConstants";
import ServiceProCard from "../../components/Client/ServiceProCard";
import CustomButton from "../../components/Form/CustomButton";
import { Pagination } from "antd";
import { useThunk } from "../../hooks/useThunk";
import { useSearchParams } from "react-router-dom";
import { RiSearchLine } from "react-icons/ri";
import { useSelector } from "react-redux";
import { fetchTaskerProfiles } from "../../store";
import Loader from "../../components/Common/Loader";
import ErrorDisplay from "../../error/ErrorDisplay";
import ServiceProCardSkeleton from "../../components/skeleton/ServiceProCardSkeleton";

// Timing constants
const TIMING = {
  URL_UPDATE_DELAY: 50,
  DATA_LOAD_CHECK_INTERVAL: 50,
  MIN_FETCH_DELAY: 100
};

const FindServicePro = ({
  wrapperClassName = "w-full h-[56px] relative rounded-[20px] font-inter font-medium text-base",
  filterClassName = "max-h-[240px] overflow-y-auto scrollbar-no-radius",
  buttonClassName = "px-10 border border-primary h-12 rounded-[20px] text-content",
  dropdownClassName = "rounded-[22px] shadow-lg",
  optionClassName = "px-10 py-3 hover:bg-gray-100 first:rounded-t-[20px] last:rounded-b-[20px] text-content",
}) => {
  const { profiles, pagination } = useSelector(
    (state) => state.user?.taskerProfiles || { profiles: [], pagination: null }
  );
  const userState = useSelector((state) => state?.auth?.user);

  const [doFetchUsers, isFetchingUsers, userError] = useThunk(fetchTaskerProfiles);
  const itemsPerPage = 5;

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );

  // Separate states for URL filters and manual filters
  const [urlFilters, setUrlFilters] = useState({
    category: null,
  });

  const [manualFilters, setManualFilters] = useState({
    city: null,
    subcity: null,
    category: null,
    skill: null,
  });

  const [taskerList, setTaskerList] = useState([]);
  const [availableSubCities, setAvailableSubCities] = useState([]);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  // Timing control refs
  const abortControllerRef = useRef(new AbortController());
  const isProcessingRef = useRef(false);
  const updateQueueRef = useRef([]);
  const requestIdRef = useRef(0);

  // load skeltons during loading time
  useEffect(() => {
    if (!isFetchingUsers && profiles) {
      setTaskerList(profiles);
    } else {
      setTaskerList([0, 0, 0, 0, 0]);
    }
  }, [profiles, isFetchingUsers]);

  // Read filters from URL on initial load
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const cityParam = searchParams.get("city");
    const subcityParam = searchParams.get("subcity");
    const skillParam = searchParams.get("skill");
    const pageParam = searchParams.get("page");
    const offsetParam = searchParams.get("offset");
    const limitParam = searchParams.get("limit");

    const newUrlFilters = {
      category: categoryParam
        ? categories.find((c) => c.name === categoryParam)
        : null,
      city: cityParam ? cities?.find((c) => c.name === cityParam) : null,
      subcity: subcityParam
        ? cities
            .find((c) => c.name === cityParam)
            ?.subcities.find((sc) => sc.name === subcityParam)
        : null,
      skill: skillParam
        ? categories
            .find((c) => c.name === categoryParam)
            ?.skills.find((s) => s.name === skillParam)
        : null,
    };

    if (newUrlFilters.city) {
      setAvailableSubCities(getSubCitiesByCity(newUrlFilters?.city?.name));
    } else {
      setAvailableSubCities([]);
    }

    if (newUrlFilters.category) {
      setAvailableSkills(getSkillsByCategory(newUrlFilters?.category?.name));
    } else {
      setAvailableSkills([]);
    }

    setUrlFilters(newUrlFilters);
    setManualFilters(newUrlFilters);

    if (pageParam) {
      setCurrentPage(Number(pageParam));
    }

    // Trigger a search on initial load if URL has filters
    setIsSearchTriggered(true);

    setFirstLoad(false);
  }, []);

  // Process update queue
  const processQueue = async () => {
    if (updateQueueRef.current.length === 0) {
      isProcessingRef.current = false;
      return;
    }

    isProcessingRef.current = true;
    const nextPage = updateQueueRef.current[0];
    
    // 1. Update URL first
    const offset = (nextPage - 1) * itemsPerPage;
    const params = new URLSearchParams(searchParams);
    params.set("page", nextPage);
    params.set("offset", offset);
    params.set("limit", itemsPerPage);
    setSearchParams(params);
    
    // 2. Wait for URL update to complete
    await new Promise(resolve => setTimeout(resolve, TIMING.URL_UPDATE_DELAY));
    
    // 3. Update state
    setCurrentPage(nextPage);
    
    // 4. Trigger data fetch
    setIsSearchTriggered(true);
    
    // 5. Wait for data to load
    await new Promise(resolve => {
      const checkDataLoaded = () => {
        if (!isFetchingUsers) {
          resolve();
        } else {
          setTimeout(checkDataLoaded, TIMING.DATA_LOAD_CHECK_INTERVAL);
        }
      };
      checkDataLoaded();
    });
    
    // 6. Remove processed item from queue
    updateQueueRef.current = updateQueueRef.current.slice(1);
    
    // 7. Process next item in queue
    processQueue();
  };

  const handlePageChange = (page) => {
    updateQueueRef.current = [...updateQueueRef.current, page];
    if (!isProcessingRef.current) {
      processQueue();
    }
  };

  // Fetch service providers with timing control
  useEffect(() => {
    let isMounted = true;
    let activeRequest = true;
    let timer;
    
    const fetchData = async () => {
      if (isSearchTriggered) {
        const currentRequestId = ++requestIdRef.current;
        
        try {
          // Cancel any pending request
          abortControllerRef.current.abort();
          abortControllerRef.current = new AbortController();
          
          const urlOffset = Number(searchParams.get("offset"));
          const urlLimit = Number(searchParams.get("limit"));
          
          const offset = urlOffset || (currentPage - 1) * itemsPerPage;
          const limit = urlLimit || itemsPerPage;

          const activeFilters = firstLoad ? urlFilters : manualFilters;

          const queryParams = {
            offset,
            limit,
            withReviewedOrdersCount: true,
            withTotalOrdersCount: true,
            city: activeFilters.city?.name || null,
            subcity: activeFilters.subcity?.name || null,
            category: activeFilters.category?.name || null,
            skill: activeFilters.skill?.name || null,
            signal: abortControllerRef.current.signal
          };

          await doFetchUsers(queryParams);
          
          if (isMounted && activeRequest && currentRequestId === requestIdRef.current) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            setIsSearchTriggered(false);
          }
        } catch (error) {
          if (error.name !== 'AbortError' && isMounted && activeRequest) {
            console.error("Failed to fetch user profiles:", error);
          }
        }
      }
    };
    
    timer = setTimeout(fetchData, TIMING.MIN_FETCH_DELAY);
    
    return () => {
      isMounted = false;
      activeRequest = false;
      clearTimeout(timer);
    };
  }, [isSearchTriggered, currentPage, searchParams]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current.abort();
      isProcessingRef.current = false;
    };
  }, []);

  // ... keep all other functions exactly the same:
  const handleSelectedCity = (city) => {
    setManualFilters((prev) => ({
      ...prev,
      city,
      subcity: null,
    }));

    if (city) {
      setAvailableSubCities(getSubCitiesByCity(city.name));
    } else {
      setAvailableSubCities([]);
    }
  };

  const handleSelectedSubCity = (subCity) => {
    setManualFilters((prev) => ({
      ...prev,
      subcity: subCity,
    }));
  };

  const handleSelectedCategory = (category) => {
    setManualFilters((prev) => ({
      ...prev,
      category,
      skill: null,
    }));

    if (category) {
      setAvailableSkills(getSkillsByCategory(category.name));
    } else {
      setAvailableSkills([]);
    }
  };

  const handleSelectedSkill = (skill) => {
    setManualFilters((prev) => ({
      ...prev,
      skill,
    }));
  };

  const handleSearchClick = () => {
    updateQueueRef.current = [];
    isProcessingRef.current = false;
    
    setCurrentPage(1);
    setIsSearchTriggered(true);
    const params = new URLSearchParams();
    if (manualFilters.city) params.set("city", manualFilters.city.name);
    if (manualFilters.subcity)
      params.set("subcity", manualFilters.subcity.name);
    if (manualFilters.category)
      params.set("category", manualFilters.category.name);
    if (manualFilters.skill) params.set("skill", manualFilters.skill.name);
    params.set("page", 1);
    params.set("offset", 0);
    params.set("limit", itemsPerPage);
    setSearchParams(params);
  };

  const getSubCitiesByCity = (cityName) => {
    const matchedCity = cities.find((city) => city.name === cityName);
    return matchedCity ? matchedCity.subcities : [];
  };

  const getSkillsByCategory = (categoryName) => {
    if (!categoryName) return [];
    const matchedCategory = categories.find((cat) => cat.name === categoryName);
    return matchedCategory ? matchedCategory.skills : [];
  };

  return (
    <div className="mt-[48px] mb-[88px] px-[20px] md:px-[40px] lg:px-[100px]">
      <h1 className="text-level-2 md:text-level-1 leading-[47.5px] font-nonito_sans font-bold text-primary text-center">
        Explore Our Service Pro
      </h1>

      {/* Filter Drop Downs */}
      <div className="mt-10 flex flex-wrap lg:flex-nowrap justify-center gap-2 w-full max-w-[62.5rem] mx-auto">
        <div className="w-full sm:w-[48%] md:w-1/3">
          <Dropdown
            options={cities}
            defaultOption={manualFilters.city}
            placeholder="City"
            idKey="name"
            displayKey="name"
            wrapperClassName={`${wrapperClassName}`}
            filterClassName={filterClassName}
            buttonClassName={buttonClassName}
            dropdownClassName={dropdownClassName}
            optionClassName={optionClassName}
            onSelect={handleSelectedCity}
          />
        </div>

        <div className="w-full sm:w-[48%] lg:w-1/3">
          <Dropdown
            options={availableSubCities}
            defaultOption={manualFilters.subcity}
            placeholder="Sub City"
            idKey="name"
            displayKey="name"
            disabled={!manualFilters.city}
            wrapperClassName={`${wrapperClassName}`}
            filterClassName={filterClassName}
            buttonClassName={`${buttonClassName} ${
              !manualFilters.city ? "opacity-50 cursor-not-allowed" : ""
            }`}
            dropdownClassName={dropdownClassName}
            optionClassName={optionClassName}
            onSelect={handleSelectedSubCity}
          />
        </div>

        <div className="w-full sm:w-[48%] md:w-1/3">
          <Dropdown
            options={categories}
            defaultOption={manualFilters.category}
            placeholder="Category"
            idKey="name"
            displayKey="name"
            wrapperClassName={`${wrapperClassName}`}
            filterClassName={filterClassName}
            buttonClassName={buttonClassName}
            dropdownClassName={dropdownClassName}
            optionClassName={optionClassName}
            onSelect={handleSelectedCategory}
          />
        </div>

        <div className="w-full sm:w-[48%] md:w-1/3">
          <Dropdown
            options={availableSkills}
            defaultOption={manualFilters.skill}
            placeholder="Skills"
            idKey="name"
            displayKey="name"
            disabled={!manualFilters.category}
            wrapperClassName={`${wrapperClassName}`}
            filterClassName={filterClassName}
            buttonClassName={`${buttonClassName} ${
              !manualFilters.category ? "opacity-50 cursor-not-allowed" : ""
            }`}
            dropdownClassName={dropdownClassName}
            optionClassName={optionClassName}
            onSelect={handleSelectedSkill}
          />
        </div>

        {/* Search Button */}
        <div className="w-full sm:w-[48%] md:w-1/3 lg:w-[15%]">
          <CustomButton
            buttonText="Search"
            icon={<RiSearchLine size={20} />}
            iconPosition="left"
            bgColor="bg-primary"
            borderColor="border-transparent"
            textWeight="font-bold text-sm"
            className="px-5 text-white rounded-[0.75rem]"
            onClick={handleSearchClick}
            loading={isFetchingUsers}
          />
        </div>
      </div>

      {/* Loading State */}
      {isFetchingUsers ? (
        <div className="mt-[40px] flex flex-col gap-6 lg:gap-12 min-h-[300px]">
          {[0, 1, 2, 3, 4].map((index) => (
            <ServiceProCardSkeleton key={index} />
          ))}
        </div>
      ) : userError ? (
        <div className="mt-[40px] max-w-[800px] mx-auto">
          <ErrorDisplay
            errorMessage={
              userError?.message || "Failed to load jobs. Please try again."
            }
            buttonText="Try Again"
            onButtonClick={() => setIsSearchTriggered(true)}
          />
        </div>
      ) : (
        /* Service Pro Cards */
        <div className="mt-[40px] flex flex-col gap-6 lg:gap-12">
          {taskerList?.length > 0 ? (
            <>
              {taskerList?.map((servicePro, index) => (
                <ServiceProCard
                  servicePro={servicePro}
                  key={servicePro.id || index}
                  loading={isFetchingUsers}
                />
              ))}

              {/* Pagination */}
              <div className="flex justify-center mt-8">
                <Pagination
                  current={currentPage}
                  total={pagination?.totalDocuments || 0}
                  pageSize={itemsPerPage}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  className="font-inter"
                  disabled={isProcessingRef.current || isFetchingUsers}
                />
              </div>
            </>
          ) : (
            <div className="py-10 text-center">
              <p className="text-lg text-gray-500 font-inter">
                No service providers found for the selected filters.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FindServicePro;